import {
  useEffect,
  useState,
  useSyncExternalStore,
  type FormEvent,
  type ReactNode,
} from "react";
import { Layout as RALayout, useLogout } from "react-admin";
import {
  AppBar,
  Box,
  Button,
  Chip,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { onLoadingChange } from "./loading";
import { authStore } from "./backoffice/client";
import { SessionGate } from "./backoffice/AuthUI";
import { BrandScopeProvider, useBrandScope } from "./backoffice/shared";
import {
  MobileModuleMenu,
  ModuleSidebar,
  SectionNavigation,
} from "./navigation/ModuleNavigation";

function BrandSelector() {
  const { brandId, setBrandId } = useBrandScope();
  const [value, setValue] = useState(brandId?.toString() ?? "");
  useEffect(() => setValue(brandId?.toString() ?? ""), [brandId]);
  const invalid =
    value !== "" &&
    (!/^\d+$/.test(value) ||
      !Number.isSafeInteger(Number(value)) ||
      Number(value) < 1);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!invalid) setBrandId(value ? Number(value) : undefined);
  };
  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{ display: "flex", alignItems: "center", gap: 1 }}
    >
      <TextField
        label="Brand ID"
        placeholder="All brands"
        size="small"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        error={invalid}
        helperText={invalid ? "Enter a positive brand ID" : undefined}
        slotProps={{ htmlInput: { inputMode: "numeric" } }}
        sx={{ width: 160 }}
      />
      <Button
        type="submit"
        size="small"
        disabled={invalid || value === (brandId?.toString() ?? "")}
      >
        Apply
      </Button>
    </Box>
  );
}
function Header() {
  const auth = useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot,
  );
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const logout = useLogout();
  return (
    <AppBar position="static" color="default">
      <Toolbar sx={{ py: 1, minHeight: 72 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ width: "100%", flexWrap: "wrap", rowGap: 1 }}
        >
          <MobileModuleMenu />
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ letterSpacing: 1 }}
          >
            ALGOL{" "}
            <Box
              component="span"
              sx={{ color: "text.secondary", fontWeight: 400 }}
            >
              Backoffice
            </Box>
          </Typography>
          <Box sx={{ flex: 1 }} />
          {pathname.startsWith("/crash") ? (
            <Chip size="small" label="Global Aviadrone diagnostics" />
          ) : auth.user?.role === "PLATFORM_ADMIN" ? (
            <BrandSelector />
          ) : (
            <Chip size="small" label={`Brand ${auth.user?.brandId ?? "—"}`} />
          )}
          <Button
            color="inherit"
            onClick={(event) => setAnchor(event.currentTarget)}
            aria-haspopup="menu"
            aria-controls={anchor ? "profile-menu" : undefined}
            aria-expanded={Boolean(anchor)}
            sx={{
              textAlign: "left",
              minWidth: 0,
              maxWidth: { xs: 180, sm: 300 },
            }}
          >
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <Typography variant="body2" noWrap>
                {auth.user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {auth.user?.role.replace(/_/g, " ")}
              </Typography>
            </Box>
          </Button>
          <Menu
            id="profile-menu"
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
          >
            <MenuItem
              component={Link}
              to="/change-password"
              onClick={() => setAnchor(null)}
            >
              Change password
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchor(null);
                void logout();
              }}
            >
              Log out
            </MenuItem>
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
const EmptySidebar = () => null;
function Shell({ children }: { children: ReactNode }) {
  const auth = useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot,
  );
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const unsubscribe = onLoadingChange(setLoading);
    return () => {
      unsubscribe();
    };
  }, []);
  return (
    <BrandScopeProvider key={auth.user?.userId}>
      <RALayout
        sidebar={EmptySidebar}
        appBar={Header}
        sx={{
          minWidth: 0,
          "& .RaLayout-appFrame": { marginTop: 0 },
          "& .RaLayout-content": {
            px: "0 !important",
            minWidth: 0,
            maxWidth: "100% !important",
          },
        }}
      >
        <Box sx={{ display: "flex", flex: 1, minWidth: 0 }}>
          <ModuleSidebar />
          <Box
            sx={{
              position: "relative",
              flex: 1,
              minWidth: 0,
              p: { xs: 1.5, md: 3 },
            }}
          >
            {loading && (
              <LinearProgress
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  height: 2,
                }}
              />
            )}
            <SectionNavigation />
            {children}
          </Box>
        </Box>
      </RALayout>
    </BrandScopeProvider>
  );
}
export function Layout({ children }: { children: ReactNode }) {
  return (
    <SessionGate>
      <Shell>{children}</Shell>
    </SessionGate>
  );
}
